import React from 'react';

/**
 * Interpreta as cores de terminal da saída dos scripts.
 *
 * Os scripts do devops colorem tudo com sequências ANSI, e jogar o texto cru
 * num `<pre>` deixava `[0;32m` espalhado no meio da leitura. Aqui a sequência
 * volta a ser o que era: a cor da linha.
 *
 * Só SGR (a que termina em `m`) vira estilo. As outras sequências CSI mexem no
 * cursor, apagam linha, movem a tela: coisas que só fazem sentido num terminal
 * de verdade e que aqui só precisam sair da frente.
 */

/** Paleta de fundo escuro, que é onde o log é lido. */
const CORES: { [codigo: number]: string } = {
    30: '#5c6370',
    31: '#e06c75',
    32: '#98c379',
    33: '#e5c07b',
    34: '#61afef',
    35: '#c678dd',
    36: '#56b6c2',
    37: '#dcdfe4',
    90: '#7f8796',
    91: '#ff7b86',
    92: '#b5e890',
    93: '#f2d08c',
    94: '#7cc4ff',
    95: '#dd94ee',
    96: '#6fd3de',
    97: '#ffffff'
};

interface Estilo {
    cor?: string;
    fundo?: string;
    negrito?: boolean;
    fraco?: boolean;
    sublinhado?: boolean;
    invertido?: boolean;
}

const VAZIO: Estilo = {};

/** Aplica um bloco de parâmetros SGR sobre o estilo corrente. */
const aplicarSgr = (estilo: Estilo, parametros: string): Estilo => {
    // `ESC[m` sem número nenhum é o mesmo que `ESC[0m`.
    const codigos = (parametros === '' ? '0' : parametros)
        .split(';')
        .map(pedaco => Number(pedaco || '0'));

    let atual: Estilo = { ...estilo };

    for (let i = 0; i < codigos.length; i++) {
        const codigo = codigos[i];

        if (codigo === 0) {
            atual = {};
        } else if (codigo === 1) {
            atual.negrito = true;
        } else if (codigo === 2) {
            atual.fraco = true;
        } else if (codigo === 4) {
            atual.sublinhado = true;
        } else if (codigo === 7) {
            atual.invertido = true;
        } else if (codigo === 22) {
            atual.negrito = false;
            atual.fraco = false;
        } else if (codigo === 24) {
            atual.sublinhado = false;
        } else if (codigo === 27) {
            atual.invertido = false;
        } else if (codigo === 39) {
            delete atual.cor;
        } else if (codigo === 49) {
            delete atual.fundo;
        } else if (CORES[codigo]) {
            atual.cor = CORES[codigo];
        } else if (codigo >= 40 && codigo <= 47) {
            atual.fundo = CORES[codigo - 10];
        } else if (codigo >= 100 && codigo <= 107) {
            atual.fundo = CORES[codigo - 10];
        } else if (codigo === 38 || codigo === 48) {
            // Cor estendida: `38;5;N` (256 cores) ou `38;2;R;G;B`.
            const modo = codigos[i + 1];
            const alvo: 'cor' | 'fundo' = codigo === 38 ? 'cor' : 'fundo';

            if (modo === 5) {
                atual[alvo] = corDe256(codigos[i + 2]);
                i += 2;
            } else if (modo === 2) {
                const r = codigos[i + 2] || 0;
                const g = codigos[i + 3] || 0;
                const b = codigos[i + 4] || 0;
                atual[alvo] = 'rgb(' + r + ',' + g + ',' + b + ')';
                i += 4;
            }
        }
    }

    return atual;
};

/** A tabela de 256 cores do xterm, calculada em vez de tabelada. */
const corDe256 = (indice: number): string => {
    if (indice === undefined || indice === null || isNaN(indice)) {
        return CORES[37];
    }
    if (indice < 16) {
        return CORES[indice < 8 ? 30 + indice : 90 + (indice - 8)] || CORES[37];
    }
    if (indice < 232) {
        const n = indice - 16;
        const nivel = (valor: number): number => (valor === 0 ? 0 : 55 + valor * 40);
        return 'rgb(' + nivel(Math.floor(n / 36)) + ','
            + nivel(Math.floor((n % 36) / 6)) + ','
            + nivel(n % 6) + ')';
    }
    const cinza = 8 + (indice - 232) * 10;
    return 'rgb(' + cinza + ',' + cinza + ',' + cinza + ')';
};

const estiloCss = (estilo: Estilo): React.CSSProperties => {
    const css: React.CSSProperties = {};

    if (estilo.invertido) {
        css.color = estilo.fundo || '#12161c';
        css.background = estilo.cor || '#dcdfe4';
    } else {
        if (estilo.cor) {
            css.color = estilo.cor;
        }
        if (estilo.fundo) {
            css.background = estilo.fundo;
        }
    }

    if (estilo.negrito) {
        css.fontWeight = 600;
    }
    if (estilo.fraco) {
        css.opacity = .7;
    }
    if (estilo.sublinhado) {
        css.textDecoration = 'underline';
    }

    return css;
};

const temEstilo = (estilo: Estilo): boolean =>
    estilo.cor !== undefined || estilo.fundo !== undefined
    || !!estilo.negrito || !!estilo.fraco || !!estilo.sublinhado || !!estilo.invertido;

// eslint-disable-next-line no-control-regex
const CSI = /\x1b\[([0-9;]*)([A-Za-z])/g;
// eslint-disable-next-line no-control-regex
const OSC = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g;
// eslint-disable-next-line no-control-regex
const SOBRAS = /\x1b[()#][0-9A-Za-z]|\x1b[0-9A-Za-z=><]/g;

/** O texto sem nenhuma sequência, para quando o que se quer é só o conteúdo. */
export const semAnsi = (texto: string | null): string => {
    if (!texto) {
        return '';
    }
    return texto.replace(OSC, '').replace(CSI, '').replace(SOBRAS, '');
};

/**
 * O texto como nós do React, com as cores aplicadas.
 *
 * @param texto saída crua, com as sequências como o script as escreveu
 * @param chave prefixo das keys, para dois blocos na mesma lista não colidirem
 */
export const pintarAnsi = (texto: string | null, chave = 'ansi'): React.ReactNode => {
    if (!texto) {
        return null;
    }

    const limpo = texto.replace(OSC, '').replace(SOBRAS, '');
    const pedacos: Array<React.ReactNode> = [];

    let estilo: Estilo = VAZIO;
    let posicao = 0;
    let indice = 0;

    CSI.lastIndex = 0;
    let achado: RegExpExecArray | null;

    const empurrar = (conteudo: string, comEstilo: Estilo) => {
        if (conteudo === '') {
            return;
        }
        if (!temEstilo(comEstilo)) {
            pedacos.push(conteudo);
            return;
        }
        pedacos.push(
            <span key={chave + '-' + (indice++)} style={estiloCss(comEstilo)}>{conteudo}</span>
        );
    };

    // eslint-disable-next-line no-cond-assign
    while ((achado = CSI.exec(limpo)) !== null) {
        empurrar(limpo.slice(posicao, achado.index), estilo);
        posicao = achado.index + achado[0].length;

        if (achado[2] === 'm') {
            estilo = aplicarSgr(estilo, achado[1]);
        }
        // Qualquer outra final (K, J, A, H...) mexe no cursor: some sem deixar
        // marca, que é o mais perto de "não fez nada" que dá aqui.
    }

    empurrar(limpo.slice(posicao), estilo);

    return pedacos;
};
